async function sha384hex(input) {
    const buffer = new TextEncoder().encode(input);
    const hash = await crypto.subtle.digest("SHA-384", buffer);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}
// Reproduces Haskell's SignP24.signatureCompute for CardPaymentFailed:
// runBuilder builds {"amount":...,"3ds":...,"method":...,"orderId":...,"sessionId":...,"errorCode":...,"errorMessage":...,"crc":...}
// then hex-encodes SHA-384 of the result.
async function computeP24CardFailedSign(fields, crc) {
    const parts = [
        `${JSON.stringify("amount")}:${JSON.stringify(fields.amount)}`,
        `${JSON.stringify("3ds")}:${JSON.stringify(fields["3ds"])}`,
        `${JSON.stringify("method")}:${JSON.stringify(fields.method)}`,
        `${JSON.stringify("orderId")}:${JSON.stringify(fields.orderId)}`,
        `${JSON.stringify("sessionId")}:${JSON.stringify(fields.sessionId)}`,
        `${JSON.stringify("errorCode")}:${JSON.stringify(fields.errorCode)}`,
        `${JSON.stringify("errorMessage")}:${JSON.stringify(fields.errorMessage)}`,
        `${JSON.stringify("crc")}:${JSON.stringify(crc)}`,
    ];
    return sha384hex("{" + parts.join(",") + "}");
}
// Reproduces Haskell's SignP24.signatureCompute for PaymentSuccess:
// runBuilder builds {"merchantId":...,"posId":...,"sessionId":...,"amount":...,"originAmount":...,"currency":...,"orderId":...,"methodId":...,"statement":...,"crc":...}
// then hex-encodes SHA-384 of the result.
async function computeP24PaymentSuccessSign(fields, crc) {
    const parts = [
        `${JSON.stringify("merchantId")}:${JSON.stringify(fields.merchantId)}`,
        `${JSON.stringify("posId")}:${JSON.stringify(fields.posId)}`,
        `${JSON.stringify("sessionId")}:${JSON.stringify(fields.sessionId)}`,
        `${JSON.stringify("amount")}:${JSON.stringify(fields.amount)}`,
        `${JSON.stringify("originAmount")}:${JSON.stringify(fields.originAmount)}`,
        `${JSON.stringify("currency")}:${JSON.stringify(fields.currency)}`,
        `${JSON.stringify("orderId")}:${JSON.stringify(fields.orderId)}`,
        `${JSON.stringify("methodId")}:${JSON.stringify(fields.methodId)}`,
        `${JSON.stringify("statement")}:${JSON.stringify(fields.statement)}`,
        `${JSON.stringify("crc")}:${JSON.stringify(crc)}`,
    ];
    return sha384hex("{" + parts.join(",") + "}");
}
function parseUuidFromUrl(url) {
    var _a;
    const uuidRegex = /[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}/gi;
    return (_a = url.match(uuidRegex)) === null || _a === void 0 ? void 0 : _a.pop();
}
const debug = {
    hello: () => {
        console.log("Hello debugging!");
    },
    simulateP24CardPaymentFailed: async (crc, options = {}) => {
        var _a;
        const { orderId = 1, errorCode = "4", errorMessage = "Declined", method = 218, } = options;
        const sessionId = parseUuidFromUrl(window.location.href);
        if (sessionId === undefined) {
            console.error("[debug] simulateP24CardPaymentFailed: could not find order UUID in URL");
            return;
        }
        const { container } = await import("tsyringe");
        const { GlobalConfig } = await import("../config");
        const { RestaumaticRPCService } = await import("../services");
        const config = container.resolve(GlobalConfig);
        const { country } = config;
        const rpcService = container.resolve(RestaumaticRPCService);
        const orderSummary = await rpcService.rpc("GetOrderSummaryRequest", {
            orderId: sessionId,
        });
        if (!orderSummary) {
            console.error("[debug] simulateP24CardPaymentFailed: could not fetch order summary");
            return;
        }
        const { paymentTotal } = orderSummary.orderInfo;
        const amountInPennies = (_a = options.amount) !== null && _a !== void 0 ? _a : Math.round(paymentTotal * 100);
        const fields = {
            amount: amountInPennies,
            "3ds": false,
            method,
            orderId,
            sessionId,
            errorCode,
            errorMessage,
        };
        const sign = await computeP24CardFailedSign(fields, crc);
        const url = `/p24/payment/status/${country}/`;
        console.log(`[debug] simulateP24CardPaymentFailed: POST ${url}`, {
            ...fields,
            sign,
        });
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...fields, sign }),
        });
        const text = await response.text();
        if (response.ok) {
            console.log("[debug] simulateP24CardPaymentFailed: success →", text);
        }
        else {
            console.error(`[debug] simulateP24CardPaymentFailed: failed ${response.status} →`, text);
        }
    },
    simulateP24Payment: async (crc, merchantId, options = {}) => {
        var _a, _b;
        const { orderId = 1 } = options;
        const sessionId = parseUuidFromUrl(window.location.href);
        if (sessionId === undefined) {
            console.error("[debug] simulateP24Payment: could not find order UUID in URL");
            return;
        }
        const { container } = await import("tsyringe");
        const { GlobalConfig } = await import("../config");
        const { RestaumaticRPCService } = await import("../services");
        const config = container.resolve(GlobalConfig);
        const { country } = config;
        const posId = (_a = options.posId) !== null && _a !== void 0 ? _a : merchantId;
        // Fetch the real paymentTotal to avoid amount mismatch (which causes PaymentDeclined)
        const rpcService = container.resolve(RestaumaticRPCService);
        const orderSummary = await rpcService.rpc("GetOrderSummaryRequest", {
            orderId: sessionId,
        });
        if (!orderSummary) {
            console.error("[debug] simulateP24Payment: could not fetch order summary");
            return;
        }
        const { paymentTotal, currency } = orderSummary.orderInfo;
        // paymentTotal is a decimal (e.g. 12.50), P24 expects pennies (integer)
        const amountInPennies = (_b = options.amount) !== null && _b !== void 0 ? _b : Math.round(paymentTotal * 100);
        const fields = {
            merchantId,
            posId,
            sessionId,
            amount: amountInPennies,
            originAmount: amountInPennies,
            currency,
            orderId,
            methodId: 1,
            statement: "Debug payment simulation",
        };
        const sign = await computeP24PaymentSuccessSign(fields, crc);
        const url = `/p24/transaction/status/${country}/`;
        console.log(`[debug] simulateP24Payment: POST ${url}`, {
            ...fields,
            sign,
        });
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...fields, sign }),
        });
        const text = await response.text();
        if (response.ok) {
            console.log("[debug] simulateP24Payment: success →", text);
        }
        else {
            console.error(`[debug] simulateP24Payment: failed ${response.status} →`, text);
        }
    },
};
export function registerDebugFunctions() {
    if (typeof window !== "undefined") {
        window.debug = debug;
    }
}
